import { Button, Snackbar } from "@mui/joy";
import { TaskStatus } from "griptonite-api-client/types/tasks";
import { useEffect, useState } from "react";

import { useVenueZonesQuery } from "@/hooks/routes";
import { useOptimisticTaskUpdateMutation } from "@/hooks/tasks";
import { useCurrentVenueUuid } from "@/hooks/venues";

import { LastTaskAction, useTaskSetOverview } from "./useTaskSetOverview";

const getMessage = (lastAction: LastTaskAction) => {
  let setDoneCount = 0;
  let stripDoneCount = 0;
  lastAction.changes.forEach((x) => {
    if (x.task.type === "SET" && x.task.status === "DONE") {
      setDoneCount++;
    }
    if (x.task.type === "STRIP" && x.task.status === "DONE") {
      stripDoneCount++;
    }
  });
  let message = "";
  if (setDoneCount > 0) {
    if (setDoneCount === 1) {
      message = "Route published.";
    } else {
      message = `${setDoneCount} routes published.`;
    }
  } else if (stripDoneCount > 0) {
    if (stripDoneCount === 1) {
      message = "Route stripped.";
    } else {
      message = `${stripDoneCount} routes stripped.`;
    }
  }
  return message;
};

const TaskStatusSnackbar = () => {
  const { lastAction } = useTaskSetOverview();

  const [open, setOpen] = useState(false);
  const venueUuid = useCurrentVenueUuid();
  const { data: zones } = useVenueZonesQuery(venueUuid);

  const message = lastAction && getMessage(lastAction);

  const hasTagWarning = !!lastAction?.changes.some(
    (x) => x.task.status === "DONE" && x.task.type === "SET" && !x.task.route?.tag,
  );
  const hasZoneWarning =
    !!zones?.length &&
    !!lastAction?.changes.some(
      (x) =>
        x.task.status === "DONE" &&
        x.task.type === "SET" &&
        x.task.route?.circuit?.uuid &&
        !x.task.route?.zone,
    );
  const hasWarning = hasTagWarning || hasZoneWarning;

  useEffect(() => {
    if (
      lastAction &&
      lastAction.mutationState === "success" &&
      lastAction.changes.find(
        (x) => (x.task.type === "SET" || x.task.type === "STRIP") && x.task.status === "DONE",
      )
    ) {
      setOpen(true);
    }
  }, [lastAction, lastAction?.mutationState, lastAction?.changes]);
  const undoMutation = useOptimisticTaskUpdateMutation(venueUuid);

  const onUndo = () => {
    if (!lastAction) return;
    // TODO: support undoing multiple actions
    const lastChange = lastAction.changes[0];
    const patch = { status: lastChange.prevStatus } as {
      status: TaskStatus;
      route?: { startTime?: null | string; endTime?: null | string };
    };
    if (lastChange.task.type === "SET") {
      patch.route = { startTime: null };
    } else if (lastChange.task.type === "STRIP") {
      patch.route = { endTime: null };
    }
    undoMutation.mutate({
      task: lastChange.task,
      payload: patch,
    });
    setOpen(false);
  };

  const warningMsgParts = [];
  if (hasTagWarning) {
    warningMsgParts.push("Tag missing");
  }
  if (hasZoneWarning) {
    warningMsgParts.push("Zone missing");
  }
  const warningMsg = warningMsgParts.length ? `(Warning: ${warningMsgParts.join(", ")})` : null;

  return (
    <Snackbar
      autoHideDuration={12000}
      open={open}
      variant={hasWarning ? "soft" : "plain"}
      color={hasWarning ? "warning" : "neutral"}
      onClose={() => setOpen(false)}
      endDecorator={
        <Button
          onClick={onUndo}
          size="sm"
          variant={hasWarning ? "solid" : "soft"}
          color={hasWarning ? "warning" : "neutral"}
        >
          Undo
        </Button>
      }
      anchorOrigin={{ vertical: "bottom", horizontal: "center" }}
    >
      {message}
      {warningMsg ? (
        <>
          {" "}
          <strong>{warningMsg}</strong>
        </>
      ) : null}
    </Snackbar>
  );
};

export default TaskStatusSnackbar;
